<template>
    <div class="main-container">
        <v-container fluid class="edit-panel overflow-auto pa-5">
            <v-form ref="form" :disabled="!record.userCanSave(authUser)" @input="$emit('input', $event)">
                <v-row dense>
                    <v-col cols="12">
                        <h4 class="mb-2">{{ $t('infos') }}</h4>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField :label="$t('title')">
                            <v-text-field
                                :value="record.title"
                                :placeholder="$t('placeholder_event_info_categories_title')"
                                dense
                                outlined
                                hide-details="auto"
                                clearable
                                :disabled="!record.canEditTitle"
                                :counter="globalParam('event.max_title_chars', 70)"
                                :rules="[
                                    getRule('required'),
                                    getRule('maxLength')(globalParam('event.max_title_chars', 70)),
                                ]"
                                @input="onInput('title', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <v-row v-if="showOnlineUrlFieldUnderTitle" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('online_event')" :label-tip="$t('online_event_help')">
                            <UrlField
                                :value="record.online_event_url"
                                :placeholder="$t('placeholder_event_info_categories_online_event_url')"
                                :rules="[isKulturstream ? getRule('required') : null].filter(Boolean)"
                                @input="onInput('online_event_url', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>

                <v-row v-if="userIsStaff() && showSocialMediaTitleField" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('socialmedia_title')">
                            <v-text-field
                                :value="record.socialmedia_title"
                                :placeholder="$t('placeholder_event_info_categories_socialmedia_title')"
                                dense
                                hide-details="auto"
                                outlined
                                clearable
                                :counter="globalParam('event.max_social_media_title_chars', 70)"
                                :rules="[getRule('maxLength')(globalParam('event.max_social_media_title_chars', 70))]"
                                :disabled="!record.canEditSocialMediaTitle"
                                @input="onInput('socialmedia_title', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="6">
                        <LabelField :label="$t('event_organizer')">
                            <EventOrganizerSelect
                                :event="record"
                                :required="eoMandatory"
                                @selected="onEventOrganizerSelect($event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="6">
                        <LabelField
                            v-if="showLocationField"
                            :label="$t('event_location')"
                            :label-tip="$t('event_location_help')"
                        >
                            <LocationSelectField
                                :location-id="record.location_id"
                                :organizer-id="record.organizer_id"
                                :placeholder="$t('placeholder_event_info_categories_location_id')"
                                :rules="record.event_class === 'permanent_exhibition' ? getRules('required') : []"
                                @selected="onEventLocationSelect($event)"
                            />
                        </LabelField>

                        <LabelField
                            v-if="showOnlineUrlFieldUnderLocation"
                            :label="$t('online_event')"
                            :label-tip="$t('online_event_help')"
                            class="mt-5"
                        >
                            <UrlField
                                :value="record.online_event_url"
                                :placeholder="$t('placeholder_event_info_categories_online_event_url')"
                                :rules="[isKulturstream ? getRule('required') : null].filter(Boolean)"
                                @input="onInput('online_event_url', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <h4 class="mb-2">{{ $t('kategorisierung') }}</h4>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('hauptsparten')">
                            <genre-group-select
                                :value="record.genre_groups"
                                :page-id="1"
                                :placeholder="$t('placeholder_event_info_categories_genre_groups')"
                                clearable
                                :rules="getRules('required')"
                                :disabled="!record.canEditGenreGroups"
                                multiple
                                chips
                                deletable-chips
                                small-chips
                                hide-details="auto"
                                @input="onInput('genre_groups', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="12" lg="6">
                        <LabelField :label="$t('frontsparte')" :label-tip="$t('frontsparte_tip')">
                            <genre-group-select
                                :value="record.main_genre_group_id"
                                :page-id="1"
                                :placeholder="$t('placeholder_event_info_categories_main_genre_group_id')"
                                :rules="getRules('required')"
                                :disabled="!record.canEditMainGenreGroup"
                                :allow-only="record.genre_groups || []"
                                clearable
                                hide-details="auto"
                                @input="onInput('main_genre_group_id', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row v-if="userIsStaff() && showEventfrogField" dense>
                    <v-col cols="12">
                        <LabelField :label="$t('eventfrog_rubrik')">
                            <eventfrog-rubric-select
                                :value="record.eventfrog_rubric_id"
                                :placeholder="$t('placeholder_event_info_categories_eventfrog_rubric_id')"
                                clearable
                                hide-details="auto"
                                @input="onInput('eventfrog_rubric_id', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
            </v-form>
        </v-container>
        <div class="info-panel overflow-auto pa-5">
            <KpAlert type="info" style="max-width: 600px">
                <span v-html="$t('event_location_info')" />
            </KpAlert>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { userIsStaff } from '@/helpers/auth';
import { Event } from '@/models/Event';
import { globalParam } from '@/helpers/globals';
import GenreGroupSelect from '@/components/genre_group/select.vue';
import EventfrogRubricSelect from '@/components/eventfrog_rubric/select.vue';
import LocationSelectField from '@/components/location/SelectField.vue';
import getRulesMixin from '@/mixins/get-form-rules';
import EventOrganizerSelect from '@/components/event/EventOrganizerSelect.vue';
import { fieldRuleValue } from '@/helpers/tools';

export default {
    name: 'EventEditInfoCategories',
    components: {
        GenreGroupSelect,
        EventfrogRubricSelect,
        LocationSelectField,
        EventOrganizerSelect,
    },
    mixins: [getRulesMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
    },

    computed: {
        ...mapState('user', {
            authUser: 'record',
        }),

        eoMandatory() {
            return fieldRuleValue('field_rules.mandatory.event.organizer_id', false);
        },

        showSocialMediaTitleField() {
            if (this.isPermanentExhibition) {
                return false;
            }
            if (this.isKulturstream) {
                return false;
            }
            if (this.isFestival) {
                return false;
            }
            if (this.isTournee) {
                return false;
            }
            return true;
        },

        showLocationField() {
            if (this.isTournee) {
                return false;
            }
            return true;
        },

        showOnlineUrlFieldUnderTitle() {
            if (this.isKulturstream) {
                return true;
            }
            return false;
        },

        showOnlineUrlFieldUnderLocation() {
            if (this.isPermanentExhibition) {
                return false;
            }
            if (this.isKulturstream) {
                return false;
            }
            if (this.isTournee) {
                return false;
            }
            return true;
        },

        showEventfrogField() {
            // see #24618: at the moment, Eventfrog Data is not shown
            return false;
            // return !this.isPermanentExhibition;
        },

        isPermanentExhibition() {
            return this.record?.event_class === Event.CLASS_PERMANENT_EXHIBITION;
        },
        isKulturstream() {
            return this.record?.event_class === Event.CLASS_STREAM;
        },
        isFestival() {
            return this.record?.event_class === Event.CLASS_FESTIVAL;
        },
        isTournee() {
            return this.record?.event_class === Event.CLASS_TOURNEE;
        },
    },

    mounted() {
        this.validateForm();
    },

    methods: {
        userIsStaff,
        globalParam,

        onInput(field, value) {
            this.record.set(field, value);

            // special handling for genre group fields 'genre_groups' and 'main_genre_group_id:'
            // The main genre group should be set to the first selected genre group:
            if (!this.record.main_genre_group_id && this.record.genre_groups?.length > 0) {
                this.record.set('main_genre_group_id', this.record.genre_groups[0]);
            }
            // main genre group can only be one of the
            // selected genre groups in the "Hauptsparten" (genre_groups) Field:
            if (!this.record.genre_groups?.includes(this.record.main_genre_group_id)) {
                this.record.set('main_genre_group_id', null);
            }
        },

        validateForm() {
            this.$refs.form.validate();
        },

        onEventOrganizerSelect(record) {
            // Nach dem event-organizer-Select setzen wir sowohl den Display-Wert (damit man gleich sieht
            // was gewählt wurde), sowie die ID:
            if (record) {
                this.onInput('organizer_id', record.id);
                this.onInput('event_organizer', record.displayTitle);
            } else {
                this.onInput('organizer_id', null);
                this.onInput('event_organizer', null);
            }
        },

        onEventLocationSelect(record) {
            if (record) {
                this.onInput('location_id', record.id);
            } else {
                this.onInput('location_id', null);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'edit info';
    grid-template-rows: auto;
    grid-template-columns: minmax(400px, 800px) minmax(300px, 1fr);
    overflow: auto;
}
.edit-panel {
    grid-area: edit;
}
.info-panel {
    grid-area: info;
    background-color: var(--toolbar-bg-color);
}
</style>
